import React, {useState} from "react";
import {NavLink as RouterNavLink, useNavigate} from "react-router-dom";
import {
    ActionIcon,
    AppShell,
    Burger,
    Button,
    Divider,
    Drawer,
    Flex,
    Group,
    Image,
    Menu,
    ScrollArea,
    Stack,
    Text,
    UnstyledButton,
    useComputedColorScheme,
    useMantineColorScheme,
    useMantineTheme
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {IconChevronDown, IconMoon, IconSun} from "@tabler/icons-react";
import {useConfig} from "../ConfigContext";

const Header = () => {
    const theme = useMantineTheme();
    const navigate = useNavigate();
    const {navigationLinks, ventures} = useConfig();
    const {setColorScheme} = useMantineColorScheme();
    const computedColorScheme = useComputedColorScheme('light');
    const [drawerOpened, setDrawerOpened] = useState(false);

    const isDark = computedColorScheme === "dark";
    const logo = isDark ? "/polyq_ai_logo_light.png" : "/polyq_ai_logo_dark.png";
    const borderColor = isDark ? "#555" : "#E3E4E6";
    const backgroundColor = isDark ? theme.colors.accent[2] : theme.colors.gray[1];
    const iconColor = isDark ? theme.colors.gray[4] : theme.colors.brand[4];
    const linkColor = isDark ? theme.colors.gray[2] : theme.colors.gray[9];
    const Icon = isDark ? IconSun : IconMoon;

    const collapseNav = useMediaQuery('(max-width: 62em)');
    const hideWordmark = useMediaQuery('(max-width: 30em)');

    const toggleColorScheme = () => setColorScheme(isDark ? 'light' : 'dark');

    const go = (link: string) => {
        setDrawerOpened(false);
        navigate(link);
    };

    const navLinkStyle = {
        color: linkColor,
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: '15px'
    };

    const desktopNav = (
        <Group gap="lg" wrap="nowrap">
            <Menu trigger="hover" openDelay={80} closeDelay={120} position="bottom-start" withinPortal>
                <Menu.Target>
                    <UnstyledButton style={navLinkStyle}>
                        <Group gap={4} wrap="nowrap">
                            Ventures
                            <IconChevronDown size={14} stroke={1.8}/>
                        </Group>
                    </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                    {ventures.map((venture) => (
                        <Menu.Item
                            key={venture.slug}
                            leftSection={React.createElement(venture.icon, {size: 18, stroke: 1.5})}
                            onClick={() => go(venture.route)}
                        >
                            <Text size="sm" fw={600}>{venture.name}</Text>
                            <Text size="xs" c="dimmed">{venture.tagline}</Text>
                        </Menu.Item>
                    ))}
                    <Menu.Divider/>
                    <Menu.Item onClick={() => go("/ventures")}>
                        <Text size="sm">All ventures</Text>
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {navigationLinks.filter((item) => !item.disabled).map((item) => (
                <RouterNavLink key={item.link} to={item.link} style={navLinkStyle}>
                    {item.label}
                </RouterNavLink>
            ))}
        </Group>
    );

    return (
        <>
            <AppShell.Header>
                <Flex justify="space-between" align="center" h="100%" px="md">
                    <Group wrap="nowrap" gap="sm">
                        {collapseNav && (
                            <Burger opened={drawerOpened} onClick={() => setDrawerOpened((o) => !o)} size="sm"/>
                        )}
                        <UnstyledButton onClick={() => go("/home")}>
                            <Group wrap="nowrap" gap="xs">
                                <Image src="/logo64.png" alt="PolyQ.ai" height={35} fit="contain"/>
                                {!hideWordmark && <Image src={logo} alt="PolyQ.ai" height={32} fit="contain"/>}
                            </Group>
                        </UnstyledButton>
                    </Group>

                    {!collapseNav && desktopNav}

                    <Group wrap="nowrap" gap="sm">
                        {!hideWordmark && (
                            <Button radius="xl" variant="filled" color="brand.6" onClick={() => go("/contact-us")}>
                                Contact us
                            </Button>
                        )}
                        <ActionIcon
                            radius="md"
                            size="lg"
                            variant="default"
                            onClick={toggleColorScheme}
                            aria-label="Toggle color scheme"
                            style={{borderColor: borderColor, backgroundColor: backgroundColor}}
                        >
                            <Icon color={iconColor}/>
                        </ActionIcon>
                    </Group>
                </Flex>
            </AppShell.Header>

            <Drawer
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
                size="80%"
                padding="md"
                title="Menu"
                zIndex={300}
                transitionProps={{duration: 150}}
                scrollAreaComponent={ScrollArea.Autosize}
            >
                <Stack gap="xs">
                    <Text fw={700} size="sm" c="dimmed">Ventures</Text>
                    {ventures.map((venture) => (
                        <UnstyledButton key={venture.slug} onClick={() => go(venture.route)}>
                            <Group gap="xs" wrap="nowrap">
                                {React.createElement(venture.icon, {size: 18, stroke: 1.5})}
                                <Text size="sm">{venture.name}</Text>
                            </Group>
                        </UnstyledButton>
                    ))}
                    <UnstyledButton onClick={() => go("/ventures")}>
                        <Text size="sm">All ventures</Text>
                    </UnstyledButton>

                    <Divider my="sm"/>

                    {navigationLinks.filter((item) => !item.disabled).map((item) => (
                        <UnstyledButton key={item.link} onClick={() => go(item.link)}>
                            <Text size="sm">{item.label}</Text>
                        </UnstyledButton>
                    ))}

                    <Button mt="md" radius="xl" color="brand.6" onClick={() => go("/contact-us")}>
                        Contact us
                    </Button>
                </Stack>
            </Drawer>
        </>
    );
};

export default Header;
